import React, { useState, useRef, useEffect } from 'react';
import {
  Container,
  Row,
  Col,
  Form,
  Button,
  Image,
  InputGroup,
  Spinner,
  Card,
  Badge,
  ProgressBar,
  Dropdown,
  Table,
  Alert,
  ListGroup
} from 'react-bootstrap';
import {
  FaSearch,
  FaPhone,
  FaVideo,
  FaUsers,
  FaUserFriends,
  FaEllipsisH,
  FaSmile,
  FaPaperclip,
  FaPaperPlane,
  FaDownload,
  FaEllipsisV,
  FaUser,
  FaThumbsUp,
  FaAddressBook,
  FaCog,
  FaGlobe,
  FaMoon,
  FaBars,
  FaTimes,
  FaChevronDown,
  FaChevronUp,
  FaCalendarAlt,
  FaFilter,
  FaChartBar,
  FaInfoCircle,
  FaPalette,
  FaLayerGroup,
  FaFile,
  FaClock,
  FaExclamationTriangle,
  FaExchangeAlt,
  FaPrint,
  FaMailBulk,
  FaEnvelope,
  FaChartLine,
  FaComments,
  FaRobot,
  FaUserClock
} from 'react-icons/fa';
import axios from 'axios';
import './ChatPage.css';
import './MessageSummaryPage.css';
import GlobalMiniSidebar from './GlobalMiniSidebar';
import messageService from '../services/messageService';
import authService from '../services/authService';
import { format, formatDistance } from 'date-fns';

const ChatPage = () => {
  const [message, setMessage] = useState('');
  const [activeTimeframe, setActiveTimeframe] = useState('Monthly');
  const [currentMonth, setCurrentMonth] = useState('March 2025');
  const [searchQuery, setSearchQuery] = useState('');
  const [activeContact, setActiveContact] = useState({ name: '', email: '' });
  const [showSidebar, setShowSidebar] = useState(false);
  const [showRecentEmails, setShowRecentEmails] = useState(true);
  const [userEmails, setUserEmails] = useState([]);
  const [recentMessages, setRecentMessages] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [searchTimeout, setSearchTimeout] = useState(null);
  const [isSearching, setIsSearching] = useState(false);
  const [summaryData, setSummaryData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const messagesEndRef = useRef(null);
  const messageContainerRef = useRef(null);
  const sidebarRef = useRef(null);
  const searchInputRef = useRef(null);

  const chatOverview = {
    totalMessages: 2486,
    totalMediaShared: 137,
    totalFilesShared: 72,
    avgResponseTime: '8 minutes',
    messagesByType: {
      text: 1986,
      media: 287,
      files: 213
    },
    mostActiveDay: 'Tuesday',
    mostActiveTime: '10:00 AM - 11:00 AM',
    trendsLastMonth: {
      messagesIncreasePercent: 18,
      responseTimeDecreasePercent: 12,
      mediaShareIncreasePercent: 23
    }
  };

  // Sample monthly activity data
  const monthlyActivity = [
    { date: 'Mar 1', messages: 35 },
    { date: 'Mar 2', messages: 28 },
    { date: 'Mar 3', messages: 42 },
    { date: 'Mar 4', messages: 56 },
    { date: 'Mar 5', messages: 45 },
    { date: 'Mar 6', messages: 32 },
    { date: 'Mar 7', messages: 19 },
    { date: 'Mar 8', messages: 29 },
    { date: 'Mar 9', messages: 38 },
    { date: 'Mar 10', messages: 44 }
  ];

  // Sample notification topics
  const notificationTopics = [
    { id: 1, name: 'Project Updates', count: 87, enabled: true },
    { id: 2, name: 'Client Feedback', count: 45, enabled: true },
    { id: 3, name: 'Team Discussions', count: 124, enabled: false },
    { id: 4, name: 'Personal', count: 53, enabled: true }
  ];

  // Toggle sidebar for mobile view
  const toggleSidebar = () => {
    setShowSidebar(!showSidebar);
  };

  // Fetch user email list on mount
  useEffect(() => {
    fetchUserEmails();
  }, []);

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return format(new Date(dateString), 'PPP p'); // e.g., "Apr 29, 2021, 5:30 PM"
    } catch (error) {
      console.error('Error formatting date:', error);
      return dateString;
    }
  };

  // Format time ago for display
  const formatTimeAgo = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return formatDistance(new Date(dateString), new Date(), { addSuffix: true });
    } catch (error) {
      console.error('Error formatting time ago:', error);
      return dateString;
    }
  };

  // Calculate message percentage
  const calculatePercentage = (part, total) => {
    if (total === 0) return 0;
    return Math.round((part / total) * 100);
  };

  // Fetch message summary data
  const fetchMessageSummary = async (email) => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await messageService.getMessageSummaryByEmail(email);
      
      if (response.error) {
        setError(response.message);
        return;
      }
      
      setSummaryData(response.data);
    } catch (err) {
      console.error('Error fetching message summary:', err);
      setError('Failed to load message summary. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Fetch user emails with optional search
  const fetchUserEmails = async (search = '') => {
    setLoadingUsers(true);
    try {
      const res = await messageService.getAllMessagesGroupedByEmail({
        search
      });
      if (!res.data.error) {
        setUserEmails(res.data);
        if (res.data.length > 0 && !activeContact.email) {
          handleUserClick(res.data[0]);
        }
      }
    } catch (err) {
      console.error('Error fetching user emails:', err);
    } finally {
      setLoadingUsers(false);
      setIsSearching(false);
    }
  };

  const handleSearchChange = e => {
    const query = e.target.value;
    setSearchQuery(query);
    setIsSearching(true);

    // Clear any existing timeout
    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }

    // Set a new timeout to search after 500ms of user stopping typing
    const timeoutId = setTimeout(() => {
      fetchUserEmails(query);

      // If we have an active contact, refresh their messages with the new search
      if (activeContact && activeContact.email) {
        handleUserClick(activeContact);
      }
    }, 500);

    setSearchTimeout(timeoutId);
  };

  // Clear search
  const clearSearch = () => {
    setSearchQuery('');
    setIsSearching(false);
    fetchUserEmails('');
    
    // Also refresh the current contact's messages without search filter
    if (activeContact && activeContact.email) {
      const tempContact = {...activeContact};
      // Small delay to ensure state updates
      setTimeout(() => {
        handleUserClick(tempContact);
      }, 100);
    }
    
    if (searchInputRef.current) {
      searchInputRef.current.focus();
    }
  };

  const handleUserClick = user => {
    setActiveContact({ name: user.name, email: user.email });
    setLoadingMessages(true);

    // Ensure we're passing the search query correctly
    const params = {};
    if (searchQuery && searchQuery.trim() !== '') {
      params.search = searchQuery.trim();
    }

    // Fetch message details
    messageService
      .getDetailedMessagesByEmail(user.email, params)
      .then(res => {
        if (!res.error) {
          setRecentMessages(res.data || []);
          // Also fetch summary data for this user
          fetchMessageSummary(user.email);
        } else {
          console.error('Error in response:', res.message);
          setRecentMessages([]);
        }
      })
      .catch(err => {
        console.error('Error fetching messages:', err);
        setRecentMessages([]);
      })
      .finally(() => {
        setLoadingMessages(false);
        if (window.innerWidth < 768) setShowSidebar(false);
      });
  };

  // Improved scroll to bottom functionality
  const scrollToBottom = () => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'end'
      });
    }
  };

  // Scroll to bottom whenever messages change
  useEffect(() => {
    scrollToBottom();
  }, [recentMessages]);

  // Responsive sidebar
  useEffect(() => {
    const handleResize = () => {
      const isMobile = window.innerWidth < 768;
      setShowSidebar(!isMobile);
      // Always show recent emails on desktop
      if (!isMobile) {
        setShowRecentEmails(true);
      }
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const handleClickOutside = event => {
      if (
        window.innerWidth < 768 &&
        sidebarRef.current &&
        !sidebarRef.current.contains(event.target) &&
        !event.target.closest('.menu-toggle')
      ) {
        setShowSidebar(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSendMessage = async e => {
    e.preventDefault();
    const trimmedMessage = message.trim();
    if (!trimmedMessage || !activeContact.email) return;

    const newMessage = {
      email: activeContact.email,
      content: trimmedMessage,
      sender_type: 'user',
      conversation_title: 'halo' // or derive from state if needed
    };

    try {
      const res = await messageService.sendMessage(newMessage);

      if (!res.error) {
        const sentMessage = {
          id: res.data?.id || Date.now(),
          sender_type: 'user',
          content: trimmedMessage,
          created_at: new Date().toISOString() // Fixed date conversion
        };
        setRecentMessages(prev => [...prev, sentMessage]);
        setMessage('');
        // Scroll to bottom after sending a message
        setTimeout(scrollToBottom, 100);
      } else {
        console.error('Send message error:', res.message);
      }
    } catch (err) {
      console.error('Send message failed:', err);
    }
  };

  // Toggle recent emails section visibility
  const toggleRecentEmails = () => {
    setShowRecentEmails(!showRecentEmails);
  };

  // Highlight search matches in text
  const highlightSearchMatch = text => {
    if (!searchQuery || !text) return text;

    // Escape special characters in the search query for regex
    const escapedQuery = searchQuery.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    
    try {
        const parts = text.split(new RegExp(`(${escapedQuery})`, 'gi'));
        return parts.map((part, i) => 
            part.toLowerCase() === searchQuery.toLowerCase() 
                ? <span key={i} className="search-highlight">{part}</span> 
                : part
        );
    } catch (error) {
        console.error('Error highlighting text:', error);
        return text;
    }
  };

  return (
    <Container fluid className='chat-container p-0'>
      <div className='d-flex chat-wrapper'>
        <GlobalMiniSidebar />

        <div className='chat-main-container'>
          {showSidebar && window.innerWidth < 768 && (
            <div
              className='sidebar-overlay'
              onClick={() => setShowSidebar(false)}
            ></div>
          )}

          <div className='d-flex h-100 position-relative'>
            {/* Sidebar */}
            <div
              ref={sidebarRef}
              className={`chat-sidebar ${
                showSidebar ? 'show-sidebar' : 'hide-sidebar'
              }`}
            >
              <div className='d-md-none sidebar-close'>
                <button
                  className='close-btn'
                  onClick={() => setShowSidebar(false)}
                >
                  <FaTimes />
                </button>
              </div>

              <div className='sidebar-header d-flex align-items-center p-3'>
                <div className='user-avatar me-3'>
                  <Image
                    src='/assets/avatars/user-1.png'
                    alt='Profile'
                    roundedCircle
                    className='avatar'
                  />
                </div>
                <div className='header-text'>
                  <h5 className='mb-0'>Chats</h5>
                </div>
              </div>

              <div className='search-box p-3'>
                <InputGroup>
                  <InputGroup.Text className='bg-light border-0'>
                    <FaSearch className='text-muted' />
                  </InputGroup.Text>
                  <Form.Control
                    placeholder='Search messages or users'
                    className='border-0 bg-light'
                    value={searchQuery}
                    onChange={handleSearchChange}
                    ref={searchInputRef}
                  />
                  {searchQuery && (
                    <InputGroup.Text
                      className='bg-light border-0 clear-search-btn'
                      onClick={clearSearch}
                    >
                      <FaTimes className='text-muted' />
                    </InputGroup.Text>
                  )}
                </InputGroup>
              </div>

              {/* Recent Email Section - Now with toggle */}
              <div className='recent-email-section px-3 pt-2'>
                <div className='d-flex justify-content-between align-items-center mb-2'>
                  <h6 className='text-muted mb-0'>
                    {searchQuery ? 'Search Results' : 'Recent Email Active'}
                    {isSearching && (
                      <Spinner animation='border' size='sm' className='ms-2' />
                    )}
                  </h6>
                  <Button
                    variant='link'
                    className='p-0 text-muted'
                    onClick={toggleRecentEmails}
                  >
                    {showRecentEmails ? <FaChevronUp /> : <FaChevronDown />}
                  </Button>
                </div>

                {showRecentEmails && (
                  <div className='recent-chats'>
                    {loadingUsers && !isSearching ? (
                      <div className='text-center my-4'>
                        <Spinner animation='border' variant='primary' />
                      </div>
                    ) : userEmails.length === 0 ? (
                      <div className='text-center py-4'>
                        <p className='text-muted'>
                          {searchQuery
                            ? 'No results found'
                            : 'No recent emails'}
                        </p>
                      </div>
                    ) : (
                      userEmails.map((user, idx) => (
                        <div
                          key={idx}
                          className={`chat-item d-flex align-items-center p-2 rounded ${
                            activeContact.email === user.email ? 'active' : ''
                          } ${user.search_match ? 'search-match' : ''}`}
                          onClick={() => handleUserClick(user)}
                        >
                          <div className='chat-avatar position-relative me-3'>
                            <div className='avatar-initial'>{user.name[0]}</div>
                          </div>
                          <div className='chat-details flex-grow-1'>
                            <div className='d-flex justify-content-between align-items-center'>
                              <h6 className='mb-0'>
                                {searchQuery
                                  ? highlightSearchMatch(user.name)
                                  : user.name}
                              </h6>
                              <span className='chat-time small text-muted'>
                                {new Date(user.last_message_time)
                                  .getHours()
                                  .toString()
                                  .padStart(2, '0')}
                                :
                                {new Date(user.last_message_time)
                                  .getMinutes()
                                  .toString()
                                  .padStart(2, '0')}
                              </span>
                            </div>
                            <p className='text-truncate mb-0 small'>
                              {user.recent_messages &&
                                user.recent_messages[0] &&
                                (searchQuery
                                  ? highlightSearchMatch(
                                      user.recent_messages[0].content.substring(
                                        0,
                                        30
                                      ) +
                                        (user.recent_messages[0].content
                                          .length > 30
                                          ? '...'
                                          : '')
                                    )
                                  : user.recent_messages[0].content.substring(
                                      0,
                                      30
                                    ) +
                                    (user.recent_messages[0].content.length > 30
                                      ? '...'
                                      : ''))}
                            </p>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Chat area */}
            {/* Main Summary Content Area */}

            <div className='chat-content-area'>
              {/* Header */}
              <div className='chat-profile-header'>
                <Row className='align-items-center m-0'>
                  <Col xs='auto' className='d-md-none menu-toggle'>
                    <button
                      className='action-icon-btn me-2'
                      onClick={toggleSidebar}
                      aria-label='Toggle sidebar'
                    >
                      <FaBars />
                    </button>
                  </Col>
                  <Col className='d-flex align-items-center'>
                    <div className='profile-info'>
                      <h4 className='mb-0 profile-name'>Chat Summary</h4>
                      <div className='online-status'>
                        <span className='text-muted'>Overview for</span>{' '}
                        {currentMonth}
                      </div>
                    </div>
                  </Col>
                  {/* <Col xs='auto' className='profile-actions'>
                    <div className='action-icons'>
                      <Dropdown>
                        <Dropdown.Toggle
                          as='button'
                          className='action-icon-btn'
                        >
                          {activeTimeframe}
                        </Dropdown.Toggle>
                        <Dropdown.Menu align='end'>
                          <Dropdown.Item
                            onClick={() => setActiveTimeframe('Daily')}
                          >
                            Daily
                          </Dropdown.Item>
                          <Dropdown.Item
                            onClick={() => setActiveTimeframe('Weekly')}
                          >
                            Weekly
                          </Dropdown.Item>
                          <Dropdown.Item
                            onClick={() => setActiveTimeframe('Monthly')}
                          >
                            Monthly
                          </Dropdown.Item>
                          <Dropdown.Item
                            onClick={() => setActiveTimeframe('Quarterly')}
                          >
                            Quarterly
                          </Dropdown.Item>
                        </Dropdown.Menu>
                      </Dropdown>
                      <button className='action-icon-btn'>
                        <FaMailBulk />
                      </button>
                      <button className='action-icon-btn'>
                        <FaEllipsisH />
                      </button>
                    </div>
                  </Col> */}
                </Row>
              </div>

              {/* Message Summary Dashboard Content */}
              <div className="chat-messages-container summary-container">
                <div className="p-3">
                  {loading ? (
                    <div className="text-center py-5">
                      <Spinner animation="border" role="status" variant="primary">
                        <span className="visually-hidden">Loading...</span>
                      </Spinner>
                      <p className="mt-3">Loading message summary data...</p>
                    </div>
                  ) : error ? (
                    <Alert variant="danger">
                      <FaInfoCircle className="me-2" />
                      {error}
                      <Button 
                        variant="outline-danger" 
                        size="sm" 
                        className="ms-3"
                        onClick={() => activeContact && activeContact.email && fetchMessageSummary(activeContact.email)}
                      >
                        Retry
                      </Button>
                    </Alert>
                  ) : !summaryData && !activeContact.email ? (
                    <Alert variant="info">
                      <FaInfoCircle className="me-2" />
                      Select a contact to view message summary.
                    </Alert>
                  ) : (
                    <>
                      {/* User Profile Card */}
                      <Row className="mb-4">
                        <Col lg={4} className="mb-4 mb-lg-0">
                          <Card>
                            <Card.Header className="bg-primary text-white">
                              <h5 className="mb-0">
                                <FaUser className="me-2" />
                                User Profile
                              </h5>
                            </Card.Header>
                            <Card.Body>
                              {summaryData && summaryData.user_profile ? (
                                <>
                                  <div className="text-center mb-4">
                                    <div className="avatar-placeholder mb-3">
                                      <span className="avatar-text">
                                        {summaryData.user_profile.name?.charAt(0) || activeContact.name?.charAt(0) || 'U'}
                                      </span>
                                    </div>
                                    <h3>{summaryData.user_profile.name || activeContact.name || 'Unknown User'}</h3>
                                    <div className="user-meta">
                                      <div>
                                        <FaEnvelope className="me-2 text-muted" />
                                        {summaryData.user_profile.email || activeContact.email}
                                      </div>
                                      {summaryData.user_profile.phone && (
                                        <div>
                                          <FaPhone className="me-2 text-muted" />
                                          {summaryData.user_profile.phone}
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                  
                                  <h5>Account Details</h5>
                                  <Table borderless size="sm">
                                    <tbody>
                                      <tr>
                                        <td><strong>Created At</strong></td>
                                        <td>{formatDate(summaryData.user_profile.profile_created_at)}</td>
                                      </tr>
                                      {summaryData.user_profile.stage && (
                                        <tr>
                                          <td><strong>Stage</strong></td>
                                          <td>
                                            <Badge bg="info">
                                              {summaryData.user_profile.stage}
                                            </Badge>
                                          </td>
                                        </tr>
                                      )}
                                      {summaryData.user_profile.purpose && (
                                        <tr>
                                          <td><strong>Purpose</strong></td>
                                          <td>{summaryData.user_profile.purpose}</td>
                                        </tr>
                                      )}
                                      <tr>
                                        <td><strong>Permissions</strong></td>
                                        <td>
                                          <Badge bg={summaryData.user_profile.permission_granted ? "success" : "warning"}>
                                            {summaryData.user_profile.permission_granted ? "Granted" : "Not Granted"}
                                          </Badge>
                                        </td>
                                      </tr>
                                    </tbody>
                                  </Table>
                                  
                                  {summaryData.user_profile.interests && summaryData.user_profile.interests.length > 0 && (
                                    <>
                                      <h5>Interests</h5>
                                      <div className="d-flex flex-wrap gap-2 mb-3">
                                        {summaryData.user_profile.interests.map((interest, index) => (
                                          <Badge key={index} bg="secondary">{interest}</Badge>
                                        ))}
                                      </div>
                                    </>
                                  )}
                                  
                                  {summaryData.user_profile.background && (
                                    <>
                                      <h5>Background</h5>
                                      <p className="mb-0">{summaryData.user_profile.background}</p>
                                    </>
                                  )}
                                </>
                              ) : (
                                <>
                                  <div className="text-center mb-4">
                                    <div className="avatar-placeholder mb-3">
                                      <span className="avatar-text">
                                        {activeContact.name?.charAt(0) || 'U'}
                                      </span>
                                    </div>
                                    <h3>{activeContact.name || 'Unknown User'}</h3>
                                    <div className="user-meta">
                                      <div>
                                        <FaEnvelope className="me-2 text-muted" />
                                        {activeContact.email}
                                      </div>
                                    </div>
                                  </div>
                                  
                                  <div className="text-center py-4">
                                    <FaInfoCircle size={32} className="text-muted mb-3" />
                                    <p>Limited profile information available</p>
                                  </div>
                                </>
                              )}
                            </Card.Body>
                          </Card>
                        </Col>
                        
                        <Col lg={8}>
                          {/* Overall Stats Cards */}
                          <Row className="mb-4">
                            <Col sm={6} xl={3} className="mb-3 mb-xl-0">
                              <Card className="text-center h-100">
                                <Card.Body>
                                  <div className="stat-icon mb-2">
                                    <FaComments className="text-primary" size={24} />
                                  </div>
                                  <h2 className="stat-number">
                                    {summaryData?.overall_stats?.total_messages || chatOverview.totalMessages}
                                  </h2>
                                  <div className="stat-label">Total Messages</div>
                                </Card.Body>
                              </Card>
                            </Col>
                            <Col sm={6} xl={3} className="mb-3 mb-xl-0">
                              <Card className="text-center h-100">
                                <Card.Body>
                                  <div className="stat-icon mb-2">
                                    <FaChartLine className="text-success" size={24} />
                                  </div>
                                  <h2 className="stat-number">
                                    {summaryData?.overall_stats?.total_conversations || userEmails.length}
                                  </h2>
                                  <div className="stat-label">Conversations</div>
                                </Card.Body>
                              </Card>
                            </Col>
                            <Col sm={6} xl={3} className="mb-3 mb-xl-0">
                              <Card className="text-center h-100">
                                <Card.Body>
                                  <div className="stat-icon mb-2">
                                    <FaUser className="text-info" size={24} />
                                  </div>
                                  <h2 className="stat-number">
                                    {summaryData?.overall_stats?.user_messages || Math.round(chatOverview.totalMessages * 0.4)}
                                  </h2>
                                  <div className="stat-label">User Messages</div>
                                  <div className="stat-percentage">
                                    {summaryData ? 
                                      calculatePercentage(summaryData.overall_stats.user_messages, summaryData.overall_stats.total_messages) : 
                                      '40'}% of total
                                  </div>
                                </Card.Body>
                              </Card>
                            </Col>
                            <Col sm={6} xl={3} className="mb-3 mb-xl-0">
                              <Card className="text-center h-100">
                                <Card.Body>
                                  <div className="stat-icon mb-2">
                                    <FaRobot className="text-warning" size={24} />
                                  </div>
                                  <h2 className="stat-number">
                                    {summaryData?.overall_stats?.ai_messages || Math.round(chatOverview.totalMessages * 0.6)}
                                  </h2>
                                  <div className="stat-label">AI Messages</div>
                                  <div className="stat-percentage">
                                    {summaryData ? 
                                      calculatePercentage(summaryData.overall_stats.ai_messages, summaryData.overall_stats.total_messages) : 
                                      '60'}% of total
                                  </div>
                                </Card.Body>
                              </Card>
                            </Col>
                          </Row>
                          
                          {/* Activity Timeline */}
                          <Card className="mb-4">
                            <Card.Header className="bg-info text-white">
                              <h5 className="mb-0">
                                <FaUserClock className="me-2" />
                                Activity Timeline
                              </h5>
                            </Card.Header>
                            <Card.Body>
                              <div className="d-flex justify-content-between mb-4">
                                <div>
                                  <h6 className="text-muted">First Interaction</h6>
                                  <p className="mb-0">
                                    <FaCalendarAlt className="me-2 text-muted" />
                                    {summaryData?.overall_stats?.first_message_time ? 
                                      formatDate(summaryData.overall_stats.first_message_time) : 
                                      'February 15, 2025'}
                                  </p>
                                </div>
                                <div className="text-end">
                                  <h6 className="text-muted">Latest Interaction</h6>
                                  <p className="mb-0">
                                    <FaClock className="me-2 text-muted" />
                                    {summaryData?.overall_stats?.latest_message_time ? 
                                      `${formatDate(summaryData.overall_stats.latest_message_time)} (${formatTimeAgo(summaryData.overall_stats.latest_message_time)})` : 
                                      'March 21, 2025 (today)'}
                                  </p>
                                </div>
                              </div>

                              <h5>Recent Messages</h5>
                              {(summaryData?.recent_messages && summaryData.recent_messages.length > 0) || 
                               (recentMessages && recentMessages.length > 0) ? (
                                <div className="messages-scroll-container" style={{ maxHeight: '300px', overflowY: 'auto' }}>
                                  <ListGroup>
                                    {(summaryData?.recent_messages || recentMessages.slice(0, 5)).map(message => (
                                      <ListGroup.Item 
                                        key={message.id}
                                        className="d-flex justify-content-between align-items-start"
                                      >
                                        <div className="me-auto">
                                          <div className="fw-bold">
                                            {message.sender_type === 'user' ? (
                                              <Badge bg="primary" className="me-2">User</Badge>
                                            ) : (
                                              <Badge bg="secondary" className="me-2">AI</Badge>
                                            )}
                                            {message.conversation_title || 'Conversation'}
                                          </div>
                                          <p className="mb-0 text-truncate" style={{ maxWidth: '500px' }}>
                                            {message.content}
                                          </p>
                                        </div>
                                        <div className="text-muted small">
                                          {formatTimeAgo(message.created_at)}
                                        </div>
                                      </ListGroup.Item>
                                    ))}
                                  </ListGroup>
                                </div>
                              ) : (
                                <Alert variant="light">No recent messages found</Alert>
                              )}
                            </Card.Body>
                          </Card>
                        </Col>
                      </Row>

                      {/* Message Activity Graph */}
                      {/* <Card className="mb-4">
                        <Card.Header className="bg-primary text-white d-flex justify-content-between align-items-center">
                          <h5 className="mb-0">
                            <FaChartBar className="me-2" />
                            Message Activity
                          </h5>
                          <Dropdown>
                            <Dropdown.Toggle variant="light" size="sm" id="dropdown-basic">
                              {activeTimeframe}
                            </Dropdown.Toggle>
                            <Dropdown.Menu>
                              <Dropdown.Item onClick={() => setActiveTimeframe('Daily')}>Daily</Dropdown.Item>
                              <Dropdown.Item onClick={() => setActiveTimeframe('Weekly')}>Weekly</Dropdown.Item>
                              <Dropdown.Item onClick={() => setActiveTimeframe('Monthly')}>Monthly</Dropdown.Item>
                            </Dropdown.Menu>
                          </Dropdown>
                        </Card.Header>
                        <Card.Body>
                          <div className="activity-graph">
                            <div className="d-flex justify-content-between activity-labels">
                              {monthlyActivity.map((day, index) => (
                                <div key={index} className="activity-day text-center">
                                  <div className="activity-bar" style={{ height: `${day.messages * 2}px` }}></div>
                                  <small className="d-block mt-2">{day.date}</small>
                                </div>
                              ))}
                            </div>
                          </div>

                          <div className="mt-4">
                            <h6>Message Distribution by Type</h6>
                            <div className="mb-3">
                              <div className="d-flex justify-content-between mb-1">
                                <span>Text ({chatOverview.messagesByType.text})</span>
                                <span>{Math.round((chatOverview.messagesByType.text / chatOverview.totalMessages) * 100)}%</span>
                              </div>
                              <ProgressBar 
                                variant="primary" 
                                now={Math.round((chatOverview.messagesByType.text / chatOverview.totalMessages) * 100)} 
                              />
                            </div>
                            <div className="mb-3">
                              <div className="d-flex justify-content-between mb-1">
                                <span>Media ({chatOverview.messagesByType.media})</span>
                                <span>{Math.round((chatOverview.messagesByType.media / chatOverview.totalMessages) * 100)}%</span>
                              </div>
                              <ProgressBar 
                                variant="success" 
                                now={Math.round((chatOverview.messagesByType.media / chatOverview.totalMessages) * 100)} 
                              />
                            </div>
                            <div>
                              <div className="d-flex justify-content-between mb-1">
                                <span>Files ({chatOverview.messagesByType.files})</span>
                                <span>{Math.round((chatOverview.messagesByType.files / chatOverview.totalMessages) * 100)}%</span>
                              </div>
                              <ProgressBar 
                                variant="info" 
                                now={Math.round((chatOverview.messagesByType.files / chatOverview.totalMessages) * 100)} 
                              />
                            </div>
                          </div>
                        </Card.Body>
                      </Card> */}

                      {/* Conversation Stats Table */}
                      <Card>
                        <Card.Header className="bg-success text-white d-flex justify-content-between align-items-center">
                          <h5 className="mb-0">
                            <FaChartLine className="me-2" />
                            Conversation Statistics
                          </h5>
                          <Badge bg="light" text="dark">
                            {summaryData?.conversation_stats?.length || userEmails.length} Conversations
                          </Badge>
                        </Card.Header>
                        <Card.Body>
                          {(summaryData?.conversation_stats && summaryData.conversation_stats.length > 0) || 
                           (userEmails && userEmails.length > 0) ? (
                            <div className="table-responsive">
                              <Table hover>
                                <thead>
                                  <tr>
                                    <th>Conversation</th>
                                    <th>Customer</th>
                                    <th>Started</th>
                                    <th>Last Activity</th>
                                    <th className="text-center">Total Messages</th>
                                    <th className="text-center">User / AI</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {summaryData?.conversation_stats ? (
                                    summaryData.conversation_stats.map(conversation => (
                                      <tr key={conversation.conversation_id}>
                                        <td>{conversation.conversation_title}</td>
                                        <td>{conversation.customer_name}</td>
                                        <td>{formatDate(conversation.conversation_start)}</td>
                                        <td>{formatTimeAgo(conversation.last_activity)}</td>
                                        <td className="text-center">{conversation.total_messages}</td>
                                        <td className="text-center">
                                          <Badge bg="primary" className="me-2">
                                            {conversation.user_messages}
                                          </Badge>
                                          <Badge bg="secondary">
                                            {conversation.ai_messages}
                                          </Badge>
                                        </td>
                                      </tr>
                                    ))
                                  ) : (
                                    // Fallback to using userEmails if no conversation_stats
                                    userEmails.map((user, idx) => (
                                      <tr key={idx} className={activeContact.email === user.email ? 'table-active' : ''}>
                                        <td>{user.name}</td>
                                        <td>{user.email}</td>
                                        <td>{new Date(user.first_message_time || user.last_message_time).toLocaleDateString()}</td>
                                        <td>{formatTimeAgo(user.last_message_time)}</td>
                                        <td className="text-center">{user.message_count || '-'}</td>
                                        <td className="text-center">
                                          <Badge bg="primary" className="me-2">
                                            {Math.round((user.message_count || 0) * 0.4)}
                                          </Badge>
                                          <Badge bg="secondary">
                                            {Math.round((user.message_count || 0) * 0.6)}
                                          </Badge>
                                        </td>
                                      </tr>
                                    ))
                                  )}
                                </tbody>
                              </Table>
                            </div>
                          ) : (
                            <Alert variant="light">No conversation statistics available</Alert>
                          )}
                        </Card.Body>
                      </Card>
                    </>
                  )}
                </div>
              </div>

            </div>
          </div>
        </div>
      </div>
    </Container>
  );
};

export default ChatPage;